import React from "react";
import Select from "react-select";
import classnames from "classnames";
import { Col, Label, FormGroup, InputGroup, InputGroupText, Input,  Card, CardHeader, CardBody} from "reactstrap";
import ImageUploadContainer from "./ImageUploadContainer";
import AdvancedPermissionContainer from "./AdvancedPermissionContainer";
import type { ImageUploadContainerProps, PickedImage } from "./ImageUploadContainer";
import type { SelectOption } from "@/utils/selectOptions";

type FieldState = "valid" | "invalid" | null;
type FieldValidation = { State: FieldState; error: string };

/** The new-user form as the fields below read it. */
type UserDraft = {
  name?: string;
  email?: string;
  emailverif?: string;
  role?: string;
  enabled?: boolean;
};

export type PersonalInformationViewProps = {
  user: UserDraft;
  image?: PickedImage;
  formValidation: {
    name: FieldValidation;
    email: FieldValidation;
    emailverif: FieldValidation;
    role: FieldValidation;
  };
  selectRoleData: SelectOption[];
  roleSelectValue: SelectOption | null;
  onInputChange: React.ChangeEventHandler<HTMLInputElement>;
  onRoleChange: (opt: SelectOption | null) => void;
  onImageUpload: ImageUploadContainerProps["ImageUpload"];
};

const PersonalInformationView = ({
  user,
  image,
  formValidation,
  selectRoleData,
  roleSelectValue,
  onInputChange,
  onRoleChange,
  onImageUpload,
}: PersonalInformationViewProps) => {
  const selectStateMsg =
    formValidation.role.State === "valid" ? (
      <div className="text-green">Looks good!</div>
    ) : formValidation.role.State === "invalid" ? (
      <div className="text-orange">Please insert user position</div>
    ) : null;

  return (
    <>
      <ImageUploadContainer ImageUpload={onImageUpload} />

      {/* Name */}
      <FormGroup className="row">
        <Label className="form-control-label" md="2">First Name & Last Name</Label>
        <Col md="10">
          <InputGroup className={classnames("input-group-merge", { focused: user.name })}>
            <InputGroupText><i className="fas fa-user" /></InputGroupText>
            <Input
              placeholder="Insert User FL Name"
              type="text"
              name="name"
              value={user.name || ""}
              onChange={onInputChange}
              required
              valid={formValidation.name.State === "valid"}
              invalid={formValidation.name.State === "invalid"}
            />
          </InputGroup>
        </Col>
      </FormGroup>

      {/* Email */}
      <FormGroup className="row">
        <Label className="form-control-label" md="2">Email</Label>
        <Col md="10">
          <InputGroup className={classnames("input-group-merge", { focused: user.email })}>
            <InputGroupText><i className="fas fa-envelope" /></InputGroupText>
            <Input
              placeholder="Insert User Email"
              type="email"
              name="email"
              value={user.email || ""}
              onChange={onInputChange}
              required
              valid={formValidation.email.State === "valid"}
              invalid={formValidation.email.State === "invalid"}
            />
          </InputGroup>
        </Col>
      </FormGroup>

      {/* Email verification */}
      <FormGroup className="row">
        <Label className="form-control-label" md="2">Email Verification</Label>
        <Col md="10">
          <InputGroup className={classnames("input-group-merge", { focused: user.emailverif })}>
            <InputGroupText><i className="fas fa-envelope" /></InputGroupText>
            <Input
              placeholder="Email verification"
              type="email"
              name="emailverif"
              value={user.emailverif || ""}
              onChange={onInputChange}
              required
              valid={formValidation.emailverif.State === "valid"}
              invalid={formValidation.emailverif.State === "invalid"}
            />
          </InputGroup>
        </Col>
      </FormGroup>

      {/* Role select */}
      <FormGroup className="row">
        <Label className="form-control-label" md="2">Select User Position</Label>
        <Col md="10">
          <Select
            value={roleSelectValue}
            name="role"
            options={selectRoleData}
            onChange={onRoleChange}
            isClearable
            isSearchable
            required
          />
          {selectStateMsg}
        </Col>
      </FormGroup>

      {/* Enabled toggle */}
      <FormGroup className="row">
        <label className="form-control-label">*Note: The user account will be active by default; you can disable access for now.</label>
        <Col md="1">
          <label className="custom-toggle custom-toggle-danger mr-1">
            <input
              type="checkbox"
              name="enabled"
              checked={!!user.enabled}
              onChange={onInputChange}
            />
            <span
              className="custom-toggle-slider rounded-circle"
              data-label-off="Yes"
              data-label-on="No"
            />
          </label>
        </Col>
      </FormGroup>

      {/* Advanced Permission */}
      <Card>
        <CardHeader><h3 className="mb-0">Access Permission</h3></CardHeader>
        <CardBody className="text-center">
          <AdvancedPermissionContainer image={image} />
        </CardBody>
      </Card>
    </>
  );
};

export default PersonalInformationView;
